import { useState } from "react";
import {
  BarChart3, Beaker, Microscope, Sparkles, ChevronDown, ChevronUp
} from "lucide-react";

/**
 * Five-section FTIR research-grade report block, rendered below the
 * Executive Summary on the demo + authed analysis pages.
 *
 * Sections (per spec):
 *   1. Instrumental metadata (editable)
 *   2. Enhanced peak table (Pos, Intensity, FWHM, Lit. ref + Δ, Vibration, Assignment, Conf%)
 *   3. Quantitative ratios (Carbonyl Index, Hydroxyl Index, CH₂/CH₃) — conditional
 *   4. Fingerprint region (500–1500 cm⁻¹) — separate peak list
 *   5. Spectrum quality assessment (banner at the top — baseline, saturation, atmospheric)
 */
export default function FtirReportSections({ report }) {
  const [showAllPeaks, setShowAllPeaks] = useState(false);

  if (!report) return null;
  const { enhanced_peaks = [], ratios = {}, fingerprint = {}, quality = {} } = report;
  const peakRows = showAllPeaks ? enhanced_peaks : enhanced_peaks.slice(0, 8);

  // ──────────────────────────────────────────────────────────────────────
  // Render sections in canonical order: 1 → 2 → 3 → 4 → 5
  //   1. Instrumental metadata
  //   2. Enhanced peak table
  //   3. Quantitative ratios (conditional)
  //   4. Fingerprint region
  //   5. Spectrum Quality Assessment (bottom summary)
  // ──────────────────────────────────────────────────────────────────────
  const qRating = quality.overall_rating || "Unknown";
  const qColor =
    qRating === "Excellent" ? "#16a34a" :
    qRating === "Good"      ? "#65a30d" :
    qRating === "Fair"      ? "#ca8a04" :
    qRating === "Poor"      ? "#dc2626" : "#9ca3af";

  return (
    <>
      {/* SECTION 2 — Enhanced peak table */}
      {enhanced_peaks.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h blue">
            <BarChart3 size={11} /> Detailed Peak Analysis (FTIR)
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="demo-table" data-testid="ftir-peak-table">
              <thead>
                <tr>
                  <th>Position (cm⁻¹)</th>
                  <th>Intensity</th>
                  <th>FWHM (cm⁻¹)</th>
                  <th>Ref. (cm⁻¹)</th>
                  <th>Δ (cm⁻¹)</th>
                  <th>Vib.</th>
                  <th>Assignment</th>
                  <th>Match %</th>
                </tr>
              </thead>
              <tbody>
                {peakRows.map((p, i) => {
                  const pos = p.position_cm ?? p.x;
                  const lit = p.literature_position_cm;
                  const dev = p.deviation_cm;
                  const conf = p.match_confidence_percent ?? parseConfidencePercent(p.confidence);
                  return (
                    <tr key={`ftir-pk-${i}`} data-testid={`ftir-peak-row-${i}`}>
                      <td><span className="demo-mono">{typeof pos === "number" ? pos.toFixed(0) : pos}</span></td>
                      <td>
                        <span className="demo-mono">{p.intensity_label || p.intensity || "—"}</span>
                        {p.transmittance_percent !== undefined && p.transmittance_percent !== null && (
                          <span style={{ display: "block", fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                            {Number(p.transmittance_percent).toFixed(1)} %T
                          </span>
                        )}
                      </td>
                      <td><span className="demo-mono">{p.fwhm_cm ?? "—"}</span></td>
                      <td data-testid={`ftir-peak-ref-${i}`}><span className="demo-mono">{lit ?? "—"}</span></td>
                      <td data-testid={`ftir-peak-delta-${i}`}>
                        <span className="demo-mono" style={{ color: dev != null && Math.abs(dev) > 30 ? "var(--warn, #ca8a04)" : "var(--text2)" }}>
                          {dev == null ? "—" : `${dev > 0 ? "+" : ""}${dev}`}
                        </span>
                      </td>
                      <td><span className="demo-mono" style={{ fontSize: 13 }}>{p.vibration_symbol || "—"}</span></td>
                      <td style={{ fontSize: 12, color: "var(--text)" }}>
                        {p.assignment || "—"}
                        {p.bond_type && (
                          <span style={{ display: "block", color: "var(--text3)", fontSize: 10, marginTop: 2 }}>
                            {p.bond_type}
                          </span>
                        )}
                      </td>
                      <td>
                        {conf !== null && conf !== undefined && !Number.isNaN(conf) ? (
                          <span
                            className="demo-mono"
                            style={{
                              padding: "2px 6px", borderRadius: 3, fontSize: 11,
                              color: conf >= 85 ? "#16a34a" : conf >= 60 ? "#ca8a04" : "#dc2626",
                              background: conf >= 85 ? "rgba(34,197,94,0.1)" : conf >= 60 ? "rgba(234,179,8,0.1)" : "rgba(239,68,68,0.1)",
                            }}
                          >
                            {conf}%
                          </span>
                        ) : <span style={{ color: "var(--text3)" }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {enhanced_peaks.length > 8 && (
            <button
              type="button"
              data-testid="ftir-toggle-all-peaks"
              onClick={() => setShowAllPeaks(v => !v)}
              style={{
                marginTop: 6, fontSize: 11, fontFamily: "var(--mono)", color: "var(--text2)",
                background: "transparent", border: "none", cursor: "pointer", display: "flex",
                alignItems: "center", gap: 4,
              }}
            >
              {showAllPeaks ? <><ChevronUp size={12} /> Show fewer</> : <><ChevronDown size={12} /> Show all {enhanced_peaks.length} peaks</>}
            </button>
          )}
        </div>
      )}

      {/* SECTION 3 — Quantitative ratios (conditional) */}
      {ratios.available && Object.keys(ratios.ratios || {}).length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h purple">
            <Beaker size={11} /> Quantitative Band Ratios
          </div>
          <div
            data-testid="ftir-ratios"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}
          >
            {Object.entries(ratios.ratios).map(([key, r]) => (
              <div
                key={key}
                data-testid={`ftir-ratio-${key}`}
                style={{
                  padding: "10px 12px", borderRadius: 6,
                  border: "1px solid var(--border, rgba(255,255,255,0.08))",
                  background: "var(--surface2, rgba(255,255,255,0.02))",
                }}
              >
                <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 1, fontFamily: "var(--mono)" }}>
                  {prettyRatioName(key)}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", fontFamily: "var(--mono)", marginTop: 4 }}>
                  {r.value}
                </div>
                <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", marginTop: 2 }}>
                  {r.formula}
                </div>
                <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 6, lineHeight: 1.4 }}>
                  {r.interpretation}
                </div>
              </div>
            ))}
          </div>
          {ratios.notes?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 10.5, color: "var(--text3)", fontFamily: "var(--mono)" }}>
              {ratios.notes.map((n, i) => <div key={i}>• {n}</div>)}
            </div>
          )}
        </div>
      )}

      {/* SECTION 4 — Fingerprint region */}
      {fingerprint.peaks?.length > 0 && (
        <div className="demo-result-block">
          <div className="demo-section-h">
            <Microscope size={11} /> Fingerprint Region ({fingerprint.region_label})
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="demo-table" data-testid="ftir-fingerprint-table">
              <thead>
                <tr>
                  <th>Position (cm⁻¹)</th>
                  <th>Intensity</th>
                  <th>FWHM</th>
                  <th>Assignment / Note</th>
                </tr>
              </thead>
              <tbody>
                {fingerprint.peaks.map((p, i) => {
                  const pos = p.position_cm ?? p.x;
                  const unassigned = !p.assignment;
                  return (
                    <tr key={`fp-${i}`} data-testid={`ftir-fingerprint-row-${i}`}>
                      <td><span className="demo-mono">{typeof pos === "number" ? pos.toFixed(0) : pos}</span></td>
                      <td><span className="demo-mono">{p.intensity_label || p.intensity || "—"}</span></td>
                      <td><span className="demo-mono">{p.fwhm_cm ?? "—"}</span></td>
                      <td style={{ fontSize: 12, color: unassigned ? "var(--warn, #ca8a04)" : "var(--text)" }}>
                        {p.assignment || "Unassigned — potential impurity / verify"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {fingerprint.match_score_percent !== undefined && fingerprint.match_score_percent !== null && (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--text2)", fontFamily: "var(--mono)" }}>
              Overall fingerprint match score: <strong>{fingerprint.match_score_percent}%</strong>
            </div>
          )}
        </div>
      )}

      {/* SECTION 5 — Spectrum Quality Assessment (bottom summary) */}
      <div className="demo-result-block">
        <div className="demo-section-h">
          <Sparkles size={11} /> Spectrum Quality Assessment
        </div>
        <div
          data-testid="ftir-quality-banner"
          data-rating={qRating.toLowerCase()}
          style={{
            padding: "12px 14px",
            borderRadius: 6,
            border: `1px solid ${qColor}55`,
            background: `${qColor}0d`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span
              data-testid="ftir-quality-rating"
              style={{
                fontFamily: "var(--mono)", fontSize: 10, letterSpacing: 1, fontWeight: 700,
                padding: "3px 8px", borderRadius: 4, color: "#fff", background: qColor,
              }}
            >
              {qRating.toUpperCase()}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
              FTIR Spectrum Quality
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 10, lineHeight: 1.5 }}>
            {quality.recommendation}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            <QualityCell label="Baseline" value={quality.baseline_quality} />
            <QualityCell
              label="Saturation"
              value={
                quality.saturated_bands?.length
                  ? `${quality.saturated_bands.length} band(s): ${quality.saturated_bands.join(", ")}`
                  : "None"
              }
              danger={Boolean(quality.saturated_bands?.length)}
            />
            <QualityCell
              label="CO₂ (2349 cm⁻¹)"
              value={quality.atmospheric_co2 ? "Detected" : "Not detected"}
              warn={Boolean(quality.atmospheric_co2)}
            />
            <QualityCell
              label="H₂O vapor"
              value={quality.atmospheric_h2o_vapor ? "Detected" : "Not detected"}
              warn={Boolean(quality.atmospheric_h2o_vapor)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── small helpers ────────────────────────────────────────────────────────

function QualityCell({ label, value, warn = false, danger = false }) {
  const accent = danger ? "#dc2626" : warn ? "#ca8a04" : "#16a34a";
  return (
    <div
      data-testid={`ftir-quality-${slug(label)}`}
      style={{
        padding: "6px 8px", borderRadius: 4,
        background: "var(--surface2, rgba(255,255,255,0.04))",
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: "var(--text2)", fontFamily: "var(--mono)", marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function prettyRatioName(key) {
  return {
    carbonyl_index: "Carbonyl Index",
    hydroxyl_index: "Hydroxyl Index",
    ch2_ch3_ratio: "CH₂ / CH₃ ratio",
  }[key] || key.replace(/_/g, " ");
}

function parseConfidencePercent(s) {
  if (typeof s !== "string") return null;
  const m = s.match(/(\d{1,3})\s*%/);
  return m ? parseInt(m[1], 10) : null;
}

function slug(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export { FtirReportSections };
